import { ConfigBatchReadResponse } from "../mocks/Config.mocks";
import { API_GATEWAY_BASE } from "../shared/config/config";

const isAuthDebugEnabled = () => {
  try {
    return window.localStorage.getItem("auth_debug") === "1";
  } catch (_) {
    return false;
  }
};

const logDownloadFileIssue = (id, message, detail) => {
  const shouldLog =
    isAuthDebugEnabled() ||
    (typeof process !== "undefined" &&
      process.env?.NODE_ENV === "development");
  if (shouldLog) {
    console.warn(`[downloadFile] ${id}: ${message}`, detail ?? "");
  }
};

const decodeJwtPayload = (token) => {
  try {
    const payloadBase64 = token.split(".")[1];
    const base64 = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

export async function getBatchConfig(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(ConfigBatchReadResponse));
  } else {
    if (isAuthDebugEnabled()) {
      const claims = decodeJwtPayload(accessToken);
      console.info("[AUTH-DEBUG] getBatchConfig token claims", {
        iss: claims?.iss,
        aud: claims?.aud,
        client_id: claims?.client_id,
        scope: claims?.scope,
        token_use: claims?.token_use,
        exp: claims?.exp,
      });
    }
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_GATEWAY_BASE}/properties/batch/read`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function postBatchConfig(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_GATEWAY_BASE}/properties/batch/write`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

const mockBannerBlob = async () => {
  const res = await fetch(`${process.env.PUBLIC_URL || ""}/banner.jpg`);
  if (!res.ok) {
    return null;
  }
  return res.blob();
};

export async function downloadFile(
  accessToken,
  id,
  impersonatedUser,
  signal,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return mockBannerBlob();
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "GET",
      headers: headers,
      ...(signal ? { signal } : {}),
    };

    return fetch(`${API_GATEWAY_BASE}/files/download/${id}`, options)
      .then(async (response) => {
        if (response.status === 200) {
          return response.blob();
        }
        logDownloadFileIssue(
          id,
          `HTTP ${response.status}`,
          response.statusText,
        );
        try {
          const ct = response.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            return await response.json();
          }
        } catch (_) {
          /* empty or non-JSON error body */
        }
        return null;
      })
      .catch((error) => {
        // No status in DevTools usually means this path (network/proxy/CORS), not HTTP 4xx/5xx.
        if (error?.name === "AbortError" || signal?.aborted) {
          throw new DOMException("The operation was aborted.", "AbortError");
        }
        logDownloadFileIssue(
          id,
          "network error (no HTTP status — check dev proxy / VPN / REACT_APP_API_INTEGRATION)",
          error?.message || error,
        );
        return null;
      });
  }
}

export async function uploadFile(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const formData = new FormData();

    formData.append("id", body.id);
    formData.append("file", body.file);

    const options = {
      method: "POST",
      headers: headers,
      body: formData,
    };

    return fetch(`${API_GATEWAY_BASE}/files/upload/`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}
